import React, { useContext, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import { PopupContext } from '../../../popups/PopupContext';
import { PrimaryPlacement } from '../../types';
import noop from 'lodash/noop';
import { setRefs } from '../../../../utils/refHelpers';
import s from './PopoverContainer.scss';

export interface Props {
  placement: PrimaryPlacement;
  style?: React.CSSProperties;
  /** ONLY for popper use, do not piggyback */
  attributes?: { [key: string]: string };
  directionalClassNames?: {
    [k in PrimaryPlacement]?: string;
  };
  wrapperClassName?: string;
  contentClassName?: string;
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
  role?: string;
  'aria-label'?: string;
  children: React.ReactNode;
  innerRef?: ((node: HTMLElement | null) => void) | React.MutableRefObject<HTMLElement | null>;
  arrow?: React.ReactNode;
  updatePositioning?: () => void;
  onClickOutside?: (event: MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

const PopoverContainer = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      style,
      placement,
      directionalClassNames,
      wrapperClassName,
      contentClassName,
      width,
      height,
      role,
      'aria-label': ariaLabel,
      children,
      innerRef,
      arrow,
      attributes,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      updatePositioning = noop,
      onClickOutside = noop,
    },
    ref
  ) => {
    const { isInPopup } = useContext(PopupContext);
    const contentRef = useRef<HTMLDivElement | null>(null);
    useResizeObserver(contentRef, updatePositioning);

    // ignore in-popup clicks if not inside one
    const outsideClickRoots = useMemo(
      () => (isInPopup ? contentRef : [contentRef, '.ReactModalPortal']),
      [isInPopup]
    );

    useOnOutsideClick(outsideClickRoots, e => onClickOutside(e), onClickOutside !== noop);

    return (
      <div
        {...attributes}
        ref={node => setRefs([ref, innerRef, contentRef], node)}
        style={{ width, height, ...style }}
        className={classnames(
          isInPopup ? s['root-in-popup'] : s['root'],
          directionalClassNames && directionalClassNames[placement],
          s['popper-wrapper'],
          wrapperClassName
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={onKeyDown}
        // So we can focus the content wrapper when it mounts if necessary
        tabIndex={-1}
        role={role}
        aria-label={ariaLabel}
      >
        <div
          className={classnames(s['popper-content'], height && s['with-scroll'], contentClassName)}
          style={{ width, height }}
        >
          {children}
        </div>
        {arrow}
      </div>
    );
  }
);
PopoverContainer.displayName = 'PopoverContainer';

export default PopoverContainer;
